import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchRoute } from "../../../api/apiFetchRoute";
import { RequestTypeOf } from "../../../generated/routes";
import {
  Building,
  DeviceWithoutLegacyPassword,
} from "../../../generated/types";
import { Button } from "../../common/Button";
import { DisplayValue } from "../../common/DisplayValue";
import { FormInput } from "../../common/FormLayout";
import { LabelParam } from "../../common/LabelParam";
import { DetailLevel, sectionToIcon } from "../../type/default";

export const BuildingSection = ({
  building,
  devices,
  detailLevel = "default",
  onClick,
  onUpdate,
}: {
  building: Building;
  devices?: DeviceWithoutLegacyPassword[];
  detailLevel?: DetailLevel;
  onClick?: () => void;
  onUpdate?: () => void;
}) => {
  const { t } = useTranslation();
  // Affiche l'enregistrement en attendant que le parent renvoie les données à jour.
  const [savedBuilding, setSavedBuilding] = useState<Building | null>(null);
  const theBuilding = savedBuilding ?? building;
  const [isEditing, setIsEditing] = useState(false);
  const [editedBuilding, setEditedBuilding] = useState(building);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    setSavedBuilding(null);
  }, [building]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedBuilding((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setSaveFailed(false);
    try {
      type BuildingRequest = RequestTypeOf<"putBuildings">;

      const body: BuildingRequest = editedBuilding;
      const response = await apiFetchRoute("putBuildings", body);

      if (!response.ok) {
        throw new Error("error-breeding-update-failed");
      }
      setIsEditing(false);
      setSavedBuilding(editedBuilding);
      onUpdate?.();
    } catch (error) {
      setSaveFailed(true);
      console.error(t("error-breeding-update-failed"), error);
    }
  };

  const startEditing = () => {
    setEditedBuilding(theBuilding);
    setSaveFailed(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setSaveFailed(false);
    setIsEditing(false);
  };

  return (
    <div>
      <div className="flex justify-between border-b border-grey-light mb-4 ">
        <div className="flex gap-4 items-center">
          {detailLevel === "maxi" && (
            <FontAwesomeIcon icon={sectionToIcon.section_building} />
          )}
          <button
            className={`${detailLevel === "mini" ? "text-sm" : detailLevel === "maxi" ? "text-lg" : "text-base"} font-oxaniumMedium ${
              onClick ? "hover:text-tertiary cursor-pointer" : "cursor-default"
            }`}
            onClick={onClick}
          >
            {theBuilding.name}
          </button>
        </div>
        {detailLevel === "maxi" && !isEditing && (
          <button
            onClick={startEditing}
            className="text-grey-medium hover:text-secondary"
          >
            <FontAwesomeIcon icon="pen-to-square" className="h-5 w-5" />
          </button>
        )}
      </div>
      <div className="space-y-4">
        {detailLevel !== "mini" && (
          <>
            {detailLevel === "maxi" &&
              (isEditing ? (
                <LabelParam label={t("building-name")}>
                  <FormInput
                    value={editedBuilding.name || ""}
                    onChange={handleChange}
                    name="name"
                    sizeInput="small"
                    className="bg-transparent border-none p-0 text-sm"
                  />
                </LabelParam>
              ) : (
                <LabelParam label={t("building-name")}>
                  <DisplayValue value={theBuilding.name ?? ""} />
                </LabelParam>
              ))}
            <LabelParam label={t("building-production-type")}>
              <DisplayValue value={theBuilding.production_type ?? ""} />
            </LabelParam>
            <LabelParam label={t("building-type")}>
              <DisplayValue value={theBuilding.type ?? ""} />
            </LabelParam>
            {isEditing ? (
              <LabelParam label={t("building-inuav")}>
                <FormInput
                  value={editedBuilding.inuav || ""}
                  onChange={handleChange}
                  name="inuav"
                  sizeInput="small"
                  className="bg-transparent border-none p-0 text-sm"
                />
              </LabelParam>
            ) : (
              <LabelParam label={t("building-inuav")}>
                <DisplayValue value={theBuilding.inuav ?? ""} />
              </LabelParam>
            )}
          </>
        )}
        {detailLevel === "maxi" && (
          <div className="space-y-4">
            <LabelParam label={t("building-shape")}>
              <DisplayValue value={theBuilding.shape ?? ""} />
            </LabelParam>

            <LabelParam label={t("building-length")}>
              <DisplayValue value={theBuilding.length ?? ""} />
            </LabelParam>

            <LabelParam label={t("building-ceiling-height")}>
              <DisplayValue value={theBuilding.roof_height ?? ""} />
            </LabelParam>

            <LabelParam label={t("building-height")}>
              <DisplayValue value={theBuilding.wall_height ?? ""} />
            </LabelParam>
          </div>
        )}

        {isEditing && saveFailed && (
          <p className="text-xs text-warning-readable">
            {t("ticket-update-error")}
          </p>
        )}
        {isEditing && (
          <div className="flex justify-end gap-4">
            <Button type="button" variant="secondary" onClick={handleCancel}>
              {t("button-cancel")}
            </Button>
            <Button type="submit" onClick={handleSubmit}>
              {t("button-save")}
            </Button>
          </div>
        )}
      </div>
      {/*devices && // TODO : Ajouter les devices avec la liste device_id
        devices.length > 0 &&
        devices
          .filter((device) => theBuilding.device.includes(device.id))
          .map((device) => (
            <DeviceSection key={device.id} device={device} detailLevel="mini" />
          ))*/}
    </div>
  );
};
